import {
  ORIGENS_LEAD,
  PERCEPCOES,
  ROTULO_PERCEPCAO,
  ROTULO_STATUS_LEAD,
  ROTULO_TEMPERATURA,
  STATUS_LEAD,
  TEMPERATURAS,
} from '@baiocchi/shared'
import { Botao, Campo, Entrada, Gaveta, Selecao, SelecaoMultipla } from '@baiocchi/ui'
import { useState } from 'react'

import {
  opcoesDeCatalogo,
  opcoesDeReferencia,
  useCatalogoAtivo,
  useEquipes,
  useUnidades,
} from '../../ganchos/usar-catalogos'
import type { Filtros } from '../../ganchos/usar-filtros-url'
import { ROTULO_ORIGEM } from '../../dominio/rotulos'

export interface PropriedadesFiltrosLeads {
  aberta: boolean
  onFechar: () => void
  filtros: Filtros
  onAplicar: (filtros: Record<string, string | undefined>) => void
  onLimpar: () => void
}

/**
 * Painel "Filtrar Leads" da TELA-04.
 *
 * Gaveta, e não modal: o usuário precisa ver a lista que os filtros vão recortar.
 *
 * Os filtros só são enviados no `Aplicar`. Aplicar a cada digitação dispararia uma
 * consulta paginada por keyset a cada tecla — e o servidor recusa filtro
 * desconhecido com `422`, então um estado intermediário inválido viraria erro na
 * tela em vez de "ainda estou escolhendo".
 */
export function GavetaFiltrosLeads({
  aberta,
  onFechar,
  filtros,
  onAplicar,
  onLimpar,
}: PropriedadesFiltrosLeads) {
  const [rascunho, setRascunho] = useState<Filtros>(filtros)

  const unidades = useUnidades()
  const equipes = useEquipes(rascunho.unidadeId)
  const pontos = useCatalogoAtivo('pontos-conversao')
  const produtos = useCatalogoAtivo('produtos-associados')
  const motivosPerda = useCatalogoAtivo('motivos-venda-nao-realizada')
  const tags = useCatalogoAtivo('tags')

  function definir(chave: string, valor: string | undefined) {
    setRascunho((atuais) => {
      const proximos = { ...atuais }
      if (valor === undefined || valor === '') delete proximos[chave]
      else proximos[chave] = valor
      return proximos
    })
  }

  function lista(chave: string): string[] {
    const valor = rascunho[chave]
    return valor ? valor.split(',').filter(Boolean) : []
  }

  return (
    <Gaveta
      aberta={aberta}
      onFechar={onFechar}
      titulo="Filtrar leads"
      descricao="Os filtros valem no seu escopo de acesso, que é aplicado antes deles."
      largura="lg"
      rodape={
        <>
          <Botao
            variante="fantasma"
            onClick={() => {
              setRascunho({})
              onLimpar()
              onFechar()
            }}
          >
            Remover filtros
          </Botao>
          <Botao variante="contorno" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao
            onClick={() => {
              // Chaves que saíram do rascunho precisam ser removidas da URL: sem
              // o `undefined` explícito, o filtro antigo continuaria na consulta.
              const combinado: Record<string, string | undefined> = {}
              for (const chave of Object.keys(filtros)) combinado[chave] = undefined
              for (const [chave, valor] of Object.entries(rascunho)) combinado[chave] = valor
              onAplicar(combinado)
              onFechar()
            }}
          >
            Aplicar filtros
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Campo rotulo="Busca" dica="Nome, telefone ou e-mail do cliente.">
          {(atributos) => (
            <Entrada
              {...atributos}
              value={rascunho.busca ?? ''}
              placeholder="Ex.: Carla, 62999000003"
              onChange={(evento) => definir('busca', evento.target.value)}
            />
          )}
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo rotulo="Situação">
            {(atributos) => (
              <SelecaoMultipla
                id={atributos.id}
                opcoes={STATUS_LEAD.map((status) => ({
                  valor: status,
                  rotulo: ROTULO_STATUS_LEAD[status],
                }))}
                selecionados={lista('status')}
                onAlterar={(valores) => definir('status', valores.join(',') || undefined)}
                placeholder="Todas as situações"
              />
            )}
          </Campo>

          <Campo rotulo="Temperatura">
            {(atributos) => (
              <SelecaoMultipla
                id={atributos.id}
                opcoes={TEMPERATURAS.map((temperatura) => ({
                  valor: temperatura,
                  rotulo: ROTULO_TEMPERATURA[temperatura],
                }))}
                selecionados={lista('temperatura')}
                onAlterar={(valores) => definir('temperatura', valores.join(',') || undefined)}
                placeholder="Todas"
              />
            )}
          </Campo>

          <Campo rotulo="Percepção">
            {(atributos) => (
              <SelecaoMultipla
                id={atributos.id}
                opcoes={PERCEPCOES.map((percepcao) => ({
                  valor: percepcao,
                  rotulo: ROTULO_PERCEPCAO[percepcao],
                }))}
                selecionados={lista('percepcao')}
                onAlterar={(valores) => definir('percepcao', valores.join(',') || undefined)}
                placeholder="Todas"
              />
            )}
          </Campo>

          <Campo rotulo="Origem">
            {(atributos) => (
              <SelecaoMultipla
                id={atributos.id}
                opcoes={ORIGENS_LEAD.map((origem) => ({
                  valor: origem,
                  rotulo: ROTULO_ORIGEM[origem],
                }))}
                selecionados={lista('origemTipo')}
                onAlterar={(valores) => definir('origemTipo', valores.join(',') || undefined)}
                placeholder="Todas"
              />
            )}
          </Campo>

          <Campo rotulo="Unidade">
            {(atributos) => (
              <Selecao
                {...atributos}
                vazio="Todas as unidades"
                value={rascunho.unidadeId ?? ''}
                opcoes={opcoesDeReferencia(unidades.data?.dados)}
                onChange={(evento) => {
                  definir('unidadeId', evento.target.value || undefined)
                  // Equipe pertence a uma unidade (I2): trocar a unidade invalida
                  // a equipe escolhida.
                  definir('equipeId', undefined)
                }}
              />
            )}
          </Campo>

          <Campo rotulo="Equipe">
            {(atributos) => (
              <Selecao
                {...atributos}
                vazio="Todas as equipes"
                value={rascunho.equipeId ?? ''}
                opcoes={opcoesDeReferencia(equipes.data?.dados)}
                onChange={(evento) => definir('equipeId', evento.target.value || undefined)}
              />
            )}
          </Campo>

          <Campo rotulo="Ponto de conversão">
            {(atributos) => (
              <Selecao
                {...atributos}
                vazio="Todos"
                value={rascunho.pontoConversaoId ?? ''}
                opcoes={opcoesDeCatalogo(pontos.data?.dados)}
                onChange={(evento) => definir('pontoConversaoId', evento.target.value || undefined)}
              />
            )}
          </Campo>

          <Campo rotulo="Produto associado">
            {(atributos) => (
              <Selecao
                {...atributos}
                vazio="Todos"
                value={rascunho.produtoAssociadoId ?? ''}
                opcoes={opcoesDeCatalogo(produtos.data?.dados)}
                onChange={(evento) =>
                  definir('produtoAssociadoId', evento.target.value || undefined)
                }
              />
            )}
          </Campo>

          <Campo rotulo="Motivo de venda não realizada">
            {(atributos) => (
              <Selecao
                {...atributos}
                vazio="Todos"
                value={rascunho.motivoVendaNaoRealizadaId ?? ''}
                opcoes={opcoesDeCatalogo(motivosPerda.data?.dados)}
                onChange={(evento) =>
                  definir('motivoVendaNaoRealizadaId', evento.target.value || undefined)
                }
              />
            )}
          </Campo>

          <Campo rotulo="Tag">
            {(atributos) => (
              <Selecao
                {...atributos}
                vazio="Todas"
                value={rascunho.tagId ?? ''}
                opcoes={opcoesDeCatalogo(tags.data?.dados)}
                onChange={(evento) => definir('tagId', evento.target.value || undefined)}
              />
            )}
          </Campo>
        </div>

        <fieldset className="rounded-md border border-borda p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-conteudo-fraco">
            Chegada do lead
          </legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo rotulo="De">
              {(atributos) => (
                <Entrada
                  {...atributos}
                  type="date"
                  value={rascunho.chegadaEmDe ?? ''}
                  onChange={(evento) => definir('chegadaEmDe', evento.target.value || undefined)}
                />
              )}
            </Campo>
            <Campo rotulo="Até">
              {(atributos) => (
                <Entrada
                  {...atributos}
                  type="date"
                  value={rascunho.chegadaEmAte ?? ''}
                  onChange={(evento) => definir('chegadaEmAte', evento.target.value || undefined)}
                />
              )}
            </Campo>
          </div>
        </fieldset>

        <fieldset className="rounded-md border border-borda p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-conteudo-fraco">
            Última interação
          </legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo rotulo="De">
              {(atributos) => (
                <Entrada
                  {...atributos}
                  type="date"
                  value={rascunho.ultimaInteracaoDe ?? ''}
                  onChange={(evento) =>
                    definir('ultimaInteracaoDe', evento.target.value || undefined)
                  }
                />
              )}
            </Campo>
            <Campo rotulo="Até">
              {(atributos) => (
                <Entrada
                  {...atributos}
                  type="date"
                  value={rascunho.ultimaInteracaoAte ?? ''}
                  onChange={(evento) =>
                    definir('ultimaInteracaoAte', evento.target.value || undefined)
                  }
                />
              )}
            </Campo>
          </div>
        </fieldset>
      </div>
    </Gaveta>
  )
}
